import {
  type Interest,
  SIGNATURE_FONTS,
  type SignatureFont,
  type UserProfile,
  getSignatureFontFamily,
  interests as interestsApi,
  users
} from '@/lib/api';
import { changePassword } from '@/lib/auth-client';
import { useDateLocale } from '@/lib/hooks/use-date-locale';
import { routes } from '@/lib/routes';
import { formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  Calendar,
  Check,
  CheckCircle,
  Download,
  Globe,
  Key,
  Loader2,
  MapPin,
  Pen,
  Save,
  Trash2,
  User
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

const LOCALES = [
  { code: 'en', name: 'English' },
  { code: 'pt', name: 'Português' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' }
];

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const dateLocale = useDateLocale();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [allInterests, setAllInterests] = useState<Record<string, Interest[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit state
  const [bio, setBio] = useState('');
  const [locale, setLocale] = useState('en');
  const [displayName, setDisplayName] = useState('');
  const [selectedInterests, setSelectedInterests] = useState<Set<string>>(new Set());
  const [signatureText, setSignatureText] = useState('');
  const [signatureFont, setSignatureFont] = useState<SignatureFont>('CAVEAT');
  const [letterFont, setLetterFont] = useState<SignatureFont>('CAVEAT');
  const [hasChanges, setHasChanges] = useState(false);
  const [displayNameError, setDisplayNameError] = useState('');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Data export state
  const [exporting, setExporting] = useState(false);

  // Delete account state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileData, interestsData] = await Promise.all([
          users.getProfile(),
          interestsApi.getGrouped()
        ]);

        setProfile(profileData);
        setBio(profileData?.bio || '');
        setLocale(profileData?.locale || 'en');
        setDisplayName(profileData?.displayName || '');
        setSignatureText(profileData?.signatureText || '');
        setSignatureFont(profileData?.signatureFont || 'CAVEAT');
        setLetterFont(profileData?.letterFont || 'CAVEAT');
        const interests = profileData?.interests ?? [];
        setSelectedInterests(new Set(interests.map((i) => i.id)));
        setAllInterests(interestsData ?? {});
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        setAllInterests({});
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!profile) return;

    const profileInterests = profile.interests ?? [];
    const originalInterestIds = new Set(profileInterests.map((i) => i.id));
    const bioChanged = bio !== (profile.bio || '');
    const localeChanged = locale !== profile.locale;
    const displayNameChanged = displayName !== (profile.displayName || '');
    const interestsChanged =
      selectedInterests.size !== originalInterestIds.size ||
      [...selectedInterests].some((id) => !originalInterestIds.has(id));
    const signatureTextChanged = signatureText !== (profile.signatureText || '');
    const signatureFontChanged = signatureFont !== (profile.signatureFont || 'CAVEAT');
    const letterFontChanged = letterFont !== (profile.letterFont || 'CAVEAT');

    setHasChanges(
      bioChanged ||
        localeChanged ||
        displayNameChanged ||
        interestsChanged ||
        signatureTextChanged ||
        signatureFontChanged ||
        letterFontChanged
    );
  }, [
    bio,
    locale,
    displayName,
    selectedInterests,
    signatureText,
    signatureFont,
    letterFont,
    profile
  ]);

  const handleSave = async () => {
    setSaving(true);
    setDisplayNameError('');
    try {
      const updated = await users.updateProfile({
        bio: bio || undefined,
        locale,
        displayName: displayName || null,
        interestIds: [...selectedInterests],
        signatureText: signatureText || null,
        signatureFont,
        letterFont
      });
      setProfile(updated);
      setDisplayName(updated.displayName || '');
      setHasChanges(false);

      // Update i18n language
      if (locale !== i18n.language) {
        i18n.changeLanguage(locale);
      }
    } catch (error) {
      // Handle display name cooldown error
      if (error instanceof Error && error.message.includes('6 months')) {
        setDisplayNameError(t('profile.displayNameCooldown'));
      } else {
        console.error('Failed to save profile:', error);
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleInterest = (id: string) => {
    setSelectedInterests((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleLocaleChange = (newLocale: string) => {
    setLocale(newLocale);
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      await users.exportData();
    } catch (error) {
      console.error('Failed to export data:', error);
    } finally {
      setExporting(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword !== confirmPassword) {
      setPasswordError(t('auth.passwordMismatch'));
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError(t('auth.weakPassword'));
      return;
    }

    setChangingPassword(true);

    try {
      await changePassword(currentPassword, newPassword);
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err instanceof Error) {
        setPasswordError(err.message);
      } else {
        setPasswordError(t('common.error'));
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setDeleteError('');
    setDeleting(true);

    try {
      await users.deleteAccount(deletePassword);
      navigate(routes.login);
    } catch (err) {
      if (err instanceof Error) {
        setDeleteError(err.message);
      } else {
        setDeleteError(t('common.error'));
      }
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-sepia animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-16">
        <p className="text-sepia">{t('common.error')}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif text-ink">{t('profile.title')}</h1>
          <p className="text-sepia/70">{t('profile.subtitle')}</p>
        </div>

        {hasChanges && (
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-gold text-white rounded-lg font-medium hover:bg-gold/90 disabled:opacity-50 transition-all"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t('profile.saveChanges')}
          </motion.button>
        )}
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-lg p-6 mb-6" style={{ boxShadow: 'var(--shadow-md)' }}>
        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-gray-warm/50 flex items-center justify-center text-2xl font-medium text-sepia">
            {(profile.displayName || profile.username).charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            {profile.displayName && (
              <h2 className="text-xl font-medium text-ink">{profile.displayName}</h2>
            )}
            <p className={profile.displayName ? 'text-sepia/70' : 'text-xl font-medium text-ink'}>
              @{profile.username}
            </p>
            <p className="text-sepia/70 text-sm">{profile.email}</p>
            <div className="flex items-center gap-4 mt-2 text-sm text-sepia/60">
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {profile.country}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {t('profile.memberSince', {
                  date: formatDistanceToNow(new Date(profile.createdAt), {
                    addSuffix: true,
                    locale: dateLocale
                  })
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Display Name */}
        <div className="mb-6">
          <label htmlFor="displayName" className="block text-sm font-medium text-sepia mb-2">
            <User className="w-4 h-4 inline mr-1" />
            {t('profile.displayName')}
          </label>
          <p className="text-xs text-sepia/60 mb-2">{t('profile.displayNameDesc')}</p>
          <input
            id="displayName"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('profile.displayNamePlaceholder')}
            maxLength={50}
            className="w-full px-4 py-2.5 border border-gray-warm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all"
          />
          <div className="flex justify-between items-center mt-1">
            <div>
              {displayNameError && <p className="text-xs text-red">{displayNameError}</p>}
              {profile?.displayNameUpdatedAt && !displayNameError && (
                <p className="text-xs text-sepia/50">
                  {t('profile.displayNameLastChanged', {
                    date: formatDistanceToNow(new Date(profile.displayNameUpdatedAt), {
                      addSuffix: true,
                      locale: dateLocale
                    })
                  })}
                </p>
              )}
            </div>
            <p className="text-xs text-sepia/50">{displayName.length}/50</p>
          </div>
        </div>

        {/* Bio */}
        <div className="mb-6">
          <label htmlFor="bio" className="block text-sm font-medium text-sepia mb-2">
            {t('profile.bio')}
          </label>
          <textarea
            id="bio"
            name="bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder={t('profile.bioPlaceholder')}
            maxLength={500}
            rows={4}
            className="w-full px-4 py-3 border border-gray-warm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all resize-none"
          />
          <p className="text-xs text-sepia/50 mt-1 text-right">{bio.length}/500</p>
        </div>

        {/* Locale */}
        <div>
          <label htmlFor="locale" className="block text-sm font-medium text-sepia mb-2">
            <Globe className="w-4 h-4 inline mr-1" />
            {t('profile.language')}
          </label>
          <p className="text-xs text-sepia/60 mb-2">{t('profile.languageDesc')}</p>
          <select
            id="locale"
            value={locale}
            onChange={(e) => handleLocaleChange(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-warm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all appearance-none bg-white"
          >
            {LOCALES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Signature Settings */}
      <div className="bg-white rounded-lg p-6 mb-6" style={{ boxShadow: 'var(--shadow-md)' }}>
        <h3 className="text-lg font-medium text-ink mb-4 flex items-center gap-2">
          <Pen className="w-5 h-5" />
          {t('profile.signature')}
        </h3>
        <p className="text-sepia/70 text-sm mb-6">{t('profile.signatureDesc')}</p>

        {/* Signature Text Input */}
        <div className="mb-6">
          <label htmlFor="signatureText" className="block text-sm font-medium text-sepia mb-2">
            {t('profile.signatureText')}
          </label>
          <input
            id="signatureText"
            type="text"
            value={signatureText}
            onChange={(e) => setSignatureText(e.target.value)}
            placeholder={t('profile.signaturePlaceholder')}
            maxLength={100}
            className="w-full px-4 py-3 border border-gray-warm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all"
          />
          <p className="text-xs text-sepia/50 mt-1 text-right">{signatureText.length}/100</p>
        </div>

        {/* Font Selection */}
        <div className="mb-6">
          <span className="block text-sm font-medium text-sepia mb-3">
            {t('profile.signatureFont')}
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {SIGNATURE_FONTS.map((font) => (
              <button
                key={font.value}
                type="button"
                onClick={() => setSignatureFont(font.value)}
                className={`p-4 rounded-lg border-2 transition-all text-left ${
                  signatureFont === font.value
                    ? 'border-blue bg-blue/5'
                    : 'border-gray-warm hover:border-sepia/50'
                }`}
              >
                <span className="text-xs text-sepia/60 block mb-1">{font.label}</span>
                <span className="text-xl text-ink" style={{ fontFamily: font.cssFamily }}>
                  {signatureText || profile?.username || 'Your Name'}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Signature Preview */}
        {signatureText && (
          <div className="border border-gray-warm rounded-lg p-6 bg-cream/50">
            <p className="text-xs text-sepia/60 uppercase tracking-wide mb-3">
              {t('profile.signaturePreview')}
            </p>
            <div className="text-right">
              <p
                className="text-2xl text-blue"
                style={{ fontFamily: getSignatureFontFamily(signatureFont) }}
              >
                {signatureText}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Letter Font Settings */}
      <div className="bg-white rounded-lg p-6 mb-6" style={{ boxShadow: 'var(--shadow-md)' }}>
        <h3 className="text-lg font-medium text-ink mb-4 flex items-center gap-2">
          <Pen className="w-5 h-5" />
          {t('profile.letterFont')}
        </h3>
        <p className="text-sepia/70 text-sm mb-6">{t('profile.letterFontDesc')}</p>

        {/* Font Selection */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {SIGNATURE_FONTS.map((font) => (
            <button
              key={font.value}
              type="button"
              onClick={() => setLetterFont(font.value)}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                letterFont === font.value
                  ? 'border-blue bg-blue/5'
                  : 'border-gray-warm hover:border-sepia/50'
              }`}
            >
              <span className="text-xs text-sepia/60 block mb-1">{font.label}</span>
              <span className="text-lg text-ink" style={{ fontFamily: font.cssFamily }}>
                {t('profile.letterFontPreview')}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Interests */}
      <div className="bg-white rounded-lg p-6" style={{ boxShadow: 'var(--shadow-md)' }}>
        <h3 className="text-lg font-medium text-ink mb-4">{t('profile.interests')}</h3>
        <p className="text-sepia/70 text-sm mb-6">{t('profile.interestsDesc')}</p>

        {Object.entries(allInterests).map(([category, categoryInterests]) => (
          <div key={category} className="mb-6 last:mb-0">
            <h4 className="text-sm font-medium text-sepia mb-3">
              {t(`interests.categories.${category}`, { defaultValue: category })}
            </h4>
            <div className="flex flex-wrap gap-2">
              {categoryInterests.map((interest) => {
                const isSelected = selectedInterests.has(interest.id);
                return (
                  <button
                    key={interest.id}
                    onClick={() => toggleInterest(interest.id)}
                    type="button"
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                      isSelected
                        ? 'bg-blue text-white'
                        : 'bg-gray-warm/30 text-sepia hover:bg-gray-warm/50'
                    }`}
                  >
                    {isSelected && <Check className="w-3.5 h-3.5" />}
                    {t(`interests.items.${interest.name}`, { defaultValue: interest.name })}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Data Export */}
      <div className="bg-white rounded-lg p-6 mt-6" style={{ boxShadow: 'var(--shadow-md)' }}>
        <h3 className="text-lg font-medium text-ink mb-2 flex items-center gap-2">
          <Download className="w-5 h-5" />
          {t('profile.dataExport')}
        </h3>
        <p className="text-sepia/70 text-sm mb-4">{t('profile.dataExportDesc')}</p>
        <button
          type="button"
          onClick={handleExportData}
          disabled={exporting}
          className="px-4 py-2 bg-ink text-cream rounded-lg font-medium hover:bg-ink/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
        >
          {exporting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('profile.downloading')}
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              {t('profile.downloadData')}
            </>
          )}
        </button>
      </div>

      {/* Change Password */}
      <div className="bg-white rounded-lg p-6 mt-6" style={{ boxShadow: 'var(--shadow-md)' }}>
        <h3 className="text-lg font-medium text-ink mb-4 flex items-center gap-2">
          <Key className="w-5 h-5" />
          {t('profile.changePassword')}
        </h3>

        {passwordSuccess && (
          <div className="bg-green/10 border border-green/20 text-green rounded-md p-3 text-sm mb-4 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            {t('profile.passwordChanged')}
          </div>
        )}

        {passwordError && (
          <div className="bg-red/10 border border-red/20 text-red rounded-md p-3 text-sm mb-4">
            {passwordError}
          </div>
        )}

        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label
              htmlFor="currentPassword"
              className="block text-sm font-medium text-sepia mb-1.5"
            >
              {t('profile.currentPassword')}
            </label>
            <input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="w-full px-4 py-2.5 border border-gray-warm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all"
            />
          </div>

          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-sepia mb-1.5">
              {t('auth.newPassword')}
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-4 py-2.5 border border-gray-warm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all"
            />
          </div>

          <div>
            <label
              htmlFor="confirmNewPassword"
              className="block text-sm font-medium text-sepia mb-1.5"
            >
              {t('auth.confirmPassword')}
            </label>
            <input
              id="confirmNewPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-4 py-2.5 border border-gray-warm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all"
            />
          </div>

          <button
            type="submit"
            disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
            className="px-6 py-2.5 bg-ink text-cream rounded-lg font-medium hover:bg-ink/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
          >
            {changingPassword ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              t('profile.updatePassword')
            )}
          </button>
        </form>
      </div>

      {/* Danger Zone - Delete Account */}
      <div
        className="bg-white rounded-lg p-6 mt-6 border-2 border-red/20"
        style={{ boxShadow: 'var(--shadow-md)' }}
      >
        <h3 className="text-lg font-medium text-red mb-2 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          {t('profile.dangerZone')}
        </h3>
        <p className="text-sepia/70 text-sm mb-4">{t('profile.deleteAccountWarning')}</p>

        {!showDeleteConfirm ? (
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 bg-red/10 text-red rounded-lg font-medium hover:bg-red/20 transition-all flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            {t('profile.deleteAccount')}
          </button>
        ) : (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="space-y-4"
          >
            <div className="bg-red/5 border border-red/20 rounded-lg p-4">
              <p className="text-sm text-red font-medium mb-2">{t('profile.deleteConfirmTitle')}</p>
              <p className="text-sm text-sepia/70">{t('profile.deleteConfirmDesc')}</p>
            </div>

            {deleteError && (
              <div className="bg-red/10 border border-red/20 text-red rounded-md p-3 text-sm">
                {deleteError}
              </div>
            )}

            <form onSubmit={handleDeleteAccount} className="space-y-4">
              <div>
                <label
                  htmlFor="deletePassword"
                  className="block text-sm font-medium text-sepia mb-1.5"
                >
                  {t('profile.confirmWithPassword')}
                </label>
                <input
                  id="deletePassword"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  required
                  placeholder={t('auth.password')}
                  className="w-full px-4 py-2.5 border border-gray-warm rounded-lg focus:outline-none focus:ring-2 focus:ring-red/30 focus:border-red transition-all"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeletePassword('');
                    setDeleteError('');
                  }}
                  className="px-4 py-2 bg-gray-warm/30 text-sepia rounded-lg font-medium hover:bg-gray-warm/50 transition-all"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={deleting || !deletePassword}
                  className="px-4 py-2 bg-red text-white rounded-lg font-medium hover:bg-red/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      {t('profile.confirmDelete')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </div>
    </div>
  );
}
